import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { searchLessons, listDomains } from "@/lib/academy.functions";
import { AcademyNav } from "@/components/academy/AcademyNav";
import { Search } from "lucide-react";
import { ACADEMY_ID, breadcrumbs, ldScript, orgRef, pageNode, personRef, seoMeta } from "@/lib/schema";

const TITLE = "Knowledge Library — Buddha KA Academy";
const DESCRIPTION =
  "Search the full Buddha KA Academy canon — Human Design, consciousness mechanics, energy systems, and the KAfrequency framework — filtered by domain and depth.";

export const Route = createFileRoute("/academy/library")({
  head: () => ({
    ...seoMeta({ title: TITLE, description: DESCRIPTION, path: "/academy/library" }),
    scripts: [
      ldScript(
        pageNode({
          type: "CollectionPage",
          id: "https://buddhaka.com/academy/library#library",
          url: "https://buddhaka.com/academy/library",
          name: TITLE,
          description: DESCRIPTION,
          about: [personRef(), orgRef()],
          extra: { isPartOf: { "@id": ACADEMY_ID } },
        }),
      ),
      ldScript(breadcrumbs([["Academy", "/academy"], ["Library", "/academy/library"]])),
    ],
  }),
  component: LibraryPage,
});


function LibraryPage() {
  const [q, setQ] = useState("");
  const [domain, setDomain] = useState<string | undefined>(undefined);
  const [difficulty, setDifficulty] = useState<string | undefined>(undefined);

  const domains = useQuery({ queryKey: ["academy", "domains"], queryFn: () => listDomains() });
  const results = useQuery({
    queryKey: ["academy", "search", q, domain ?? "any"],
    queryFn: () => searchLessons({ data: { q, domainSlug: domain } }),
  });

  const filtered = (results.data ?? []).filter((l) => !difficulty || l.difficulty === difficulty);
  const domainSlugById = new Map((domains.data ?? []).map((d) => [d.id, d.slug]));

  return (
    <>
      <AcademyNav />
      <div className="min-h-dvh bg-obsidian text-foreground">
        <section className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
          <p className="text-xs uppercase tracking-[0.3em] text-gold">Library</p>
          <h1 className="mt-2 font-serif text-4xl sm:text-5xl text-gold-luminous">Search the canon.</h1>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <label className="relative flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-foreground/50" size={16} />
              <input
                value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search lessons, concepts, prompts…"
                className="w-full rounded-full border hairline bg-black/40 pl-9 pr-4 py-3 text-sm outline-none focus:border-gold"
              />
            </label>
            <select aria-label="Filter by domain" value={domain ?? ""} onChange={(e) => setDomain(e.target.value || undefined)}
              className="rounded-full border hairline bg-black/40 px-4 py-3 text-sm">
              <option value="">All domains</option>
              {(domains.data ?? []).map((d) => (
                <option key={d.id} value={d.slug}>{d.name}</option>
              ))}
            </select>
            <select aria-label="Filter by difficulty" value={difficulty ?? ""} onChange={(e) => setDifficulty(e.target.value || undefined)}
              className="rounded-full border hairline bg-black/40 px-4 py-3 text-sm">
              <option value="">Any level</option>
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
              <option value="master">Master</option>
            </select>
          </div>

          <ul className="mt-8 grid gap-3">
            {results.isLoading && <li className="text-sm text-foreground/60">Searching…</li>}
            {!results.isLoading && filtered.length === 0 && (
              <li className="text-sm text-foreground/60">No lessons match yet. Try a different term.</li>
            )}
            {filtered.map((l) => {
              const dslug = domainSlugById.get(l.domain_id ?? "") ?? "human-design";
              return (
                <li key={l.id}>
                  <Link to="/academy/$domain/lesson/$lesson" params={{ domain: dslug, lesson: l.slug }}
                    className="block rounded-2xl border hairline p-4 transition hover:border-gold/60">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <h3 className="font-serif text-lg">{l.title}</h3>
                      <span className="text-[10px] uppercase tracking-widest text-gold">{l.difficulty} · {l.tier}</span>
                    </div>
                    {l.summary && <p className="mt-1 line-clamp-2 text-sm text-foreground/70">{l.summary}</p>}
                    <p className="mt-2 text-xs text-foreground/50">{l.est_minutes} min</p>
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>
      </div>
    </>
  );
}
